import { Search, Users, Target, FileText, Mail, Globe } from 'lucide-react'
import type { Service } from '../types'

const Services = () => {
  const services: Service[] = [
    {
      id: 'seo',
      title: 'SEO & Search Marketing',
      description: 'Dominate search results and drive organic traffic with our proven SEO strategies.',
      icon: 'Search',
      features: [
        'Keyword Research & Strategy',
        'On-Page & Technical SEO',
        'Link Building & Authority',
        'Local SEO Optimization'
      ]
    },
    {
      id: 'social',
      title: 'Social Media Marketing',
      description: 'Build brand awareness and engage audiences across all major social platforms.',
      icon: 'Users',
      features: [
        'Content Strategy & Creation',
        'Community Management',
        'Social Media Advertising',
        'Influencer Partnerships'
      ]
    },
    {
      id: 'ppc',
      title: 'PPC & Google Ads',
      description: 'Maximize ROI with targeted advertising campaigns that convert visitors into customers.',
      icon: 'Target',
      features: [
        'Campaign Strategy & Setup',
        'Ad Copy & Creative Design',
        'Bid Management & Optimization',
        'Performance Tracking & Reporting'
      ]
    },
    {
      id: 'content',
      title: 'Content Marketing',
      description: 'Attract and nurture leads with valuable content that establishes your authority.',
      icon: 'FileText',
      features: [
        'Content Strategy Development',
        'Blog Writing & SEO Articles',
        'Video & Visual Content',
        'Content Distribution Strategy'
      ]
    },
    {
      id: 'email',
      title: 'Email Marketing Automation',
      description: 'Nurture leads and retain customers with personalized email marketing campaigns.',
      icon: 'Mail',
      features: [
        'Email Campaign Design',
        'Marketing Automation Workflows',
        'List Segmentation & Targeting',
        'A/B Testing & Optimization'
      ]
    },
    {
      id: 'web',
      title: 'Web Design & Development',
      description: 'Create conversion-optimized websites that turn visitors into paying customers.',
      icon: 'Globe',
      features: [
        'Responsive Web Design',
        'Conversion Rate Optimization',
        'E-commerce Development',
        'Website Speed & Performance'
      ]
    }
  ]

  const getIcon = (iconName: string) => {
    const icons = {
      Search: Search,
      Users: Users,
      Target: Target,
      FileText: FileText,
      Mail: Mail,
      Globe: Globe
    }
    const IconComponent = icons[iconName as keyof typeof icons]
    return IconComponent ? <IconComponent size={32} /> : <Search size={32} />
  }

  return (
    <section id="services" className="section-spacing bg-white">
      <div className="max-w-7xl mx-auto container-padding">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Comprehensive Digital Marketing Services
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            From SEO and PPC to social media and content marketing, we provide 
            full-service digital solutions that drive real business results.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {services.map((service) => (
            <div
              key={service.id}
              className="bg-white border border-gray-200 rounded-xl p-8 hover:shadow-lg transition-shadow duration-300 group"
            >
              {/* Icon */}
              <div className="w-16 h-16 bg-primary-100 rounded-lg flex items-center justify-center mb-6 group-hover:bg-primary group-hover:text-white transition-all duration-300">
                <div className="text-primary group-hover:text-white">
                  {getIcon(service.icon)}
                </div>
              </div>

              {/* Content */}
              <h3 className="text-xl font-semibold text-gray-900 mb-3">
                {service.title}
              </h3>
              
              <p className="text-gray-600 mb-6 leading-relaxed">
                {service.description}
              </p>

              {/* Features */}
              <ul className="space-y-2">
                {service.features.map((feature, index) => (
                  <li key={index} className="flex items-center text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-primary rounded-full mr-3 flex-shrink-0"></div>
                    {feature}
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <div className="mt-6 pt-6 border-t border-gray-100">
                <button className="text-primary font-semibold hover:text-primary-600 transition-colors duration-200 flex items-center gap-2">
                  Learn More
                  <div className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1">
                    →
                  </div>
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <p className="text-gray-600 mb-6">
            Ready to transform your digital presence?
          </p>
          <button 
            onClick={() => {
              const element = document.querySelector('#contact')
              if (element) element.scrollIntoView({ behavior: 'smooth' })
            }}
            className="btn-primary text-lg px-8 py-4"
          >
            Get Your Custom Strategy
          </button>
        </div>
      </div>
    </section>
  )
}

export default Services